'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Calendar, MapPin } from 'lucide-react'
import { eventsApi, competitionsApi, Event, PublicCompetition } from '@/lib/api'

export default function EventsPage() {
  const [events, setEvents] = useState<Event[]>([])
  const [competitionsByEvent, setCompetitionsByEvent] = useState<Record<string, PublicCompetition[]>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([eventsApi.listPublic(), competitionsApi.listPublic()])
      .then(([evs, comps]) => {
        setEvents(evs)
        const grouped: Record<string, PublicCompetition[]> = {}
        for (const c of comps) {
          if (c.eventId) {
            grouped[c.eventId] = grouped[c.eventId] ?? []
            grouped[c.eventId].push(c)
          }
        }
        setCompetitionsByEvent(grouped)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  if (loading)
    return (
      <div className="p-6 flex justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )

  return (
    <main className="flex min-h-screen flex-col">
      {/* Header */}
      <nav className="flex items-center justify-between border-b border-border px-6 py-4">
        <Link href="/" className="text-2xl font-bold text-primary">
          Cosmicon Events
        </Link>
        <div className="flex gap-4">
          <Button variant="ghost" asChild>
            <Link href="/">Home</Link>
          </Button>
          <Button asChild>
            <Link href="/register">Register</Link>
          </Button>
        </div>
      </nav>

      {/* Page Content */}
      <div className="flex-1 px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Upcoming Events</h1>
          <p className="mt-2 text-muted-foreground">
            Browse and register for cosplay events in your area
          </p>
        </div>

        {/* Events Grid */}
        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
          {events.map((event) => (
            <div
              key={event.id}
              className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold">{event.name}</h2>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary capitalize">
                      {event.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {event.description && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {event.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span>
                    {new Date(event.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                {event.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span>{event.location}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-4 border-t border-border">
                {(competitionsByEvent[event.id] ?? []).map((comp) => (
                  <Button key={comp.id} variant="outline" className="w-full justify-start" asChild>
                    <Link href={`/register/${comp.id}`}>
                      Register as Competitor — {comp.name}
                      {comp.category && (
                        <span className="ml-1 text-xs text-muted-foreground capitalize">
                          ({comp.category.replace(/_/g, ' ')})
                        </span>
                      )}
                    </Link>
                  </Button>
                ))}
                <Button className="w-full" asChild>
                  <Link href={`/events/${event.id}`}>View Event & Other Registrations</Link>
                </Button>
              </div>
            </div>
          ))}
        </div>

        {events.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border py-12">
            <p className="text-muted-foreground">No events scheduled yet</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 px-6 py-8 text-center text-sm text-muted-foreground">
        <p>&copy; 2024 Cosmicon Events</p>
      </footer>
    </main>
  )
}
